import { createSelector } from 'reselect';
import { includes, path } from 'ramda';
import { CURRENCY_TRANSLATED, UserRole } from '@enmon/commons/lib/enmon';

const selectRoute = state => state.router;

export const selectLocation = createSelector(selectRoute, routeState => routeState.location);

/**
 * @param {Partial<Pick<import('@enmon/commons/lib/enmon').SerializedUser, 'userType'>> | undefined} user
 * @returns {boolean}
 */
export const isMagic = user => user?.userType === UserRole.Magic;
/**
 * @param {Partial<Pick<import('@enmon/commons/lib/enmon').SerializedUser, 'userType'>> | undefined} user
 * @returns {boolean}
 */
export const isAdmin = user => user?.userType === UserRole.Admin;
/**
 * @param {Partial<Pick<import('@enmon/commons/lib/enmon').SerializedUser, 'userType'>> | undefined} user
 * @returns {boolean}
 */
export const isTenant = user => user?.userType === UserRole.Tenant;

/**
 * @param {import('@/reducers').StaticRootState} state
 * @returns {'magic' | 'parent' | 'child'}
 * @deprecated Use `useAppContext` hook instead.
 */
export const getAppContext = state => {
  const { customer } = state.auth;

  if (!customer) return 'magic';
  if (customer?.isParent) return 'parent';
  return 'child';
};

/**
 * @returns {(import('@/services/api/users').PopulatedUser) | undefined}
 * @deprecated Use `useActiveUser` hook instead.
 */
export const selectActiveUser = state => path(['auth', 'user'], state);

/**
 * @param {import('@/reducers').StaticRootState} state
 * @returns {import('@enmon/commons/lib/enmon').SerializedCustomerMe['customer'] | undefined}
 * @deprecated Use `useActiveCustomer` hook instead.
 */
export const selectActiveCustomer = state => state.auth.customer;

/**
 * @returns {(import('@enmon/commons/lib/enmon').SerializedCustomer['_id']) | undefined}
 * @deprecated Use `useCustomerId` hook instead.
 */
export const selectCustomerId = state => path(['auth', 'customer', '_id'], state);

/**
 * @returns {import('@enmon/commons/lib/enmon/shared').Country | undefined}
 * @deprecated Use `useCustomerCountry` hook instead.
 */
export const selectCustomerCountry = state => path(['auth', 'customer', 'country'], state);

/**
 * @param {unknown} state
 * @returns {import('@enmon/commons/lib/enmon').Feature[]}
 */
const getFeatures = state => path(['auth', 'customer', 'settings', 'features'], state) || [];

/**
 * @deprecated Use `useHasFeature` hook instead.
 */
export const selectHasFeature = createSelector(
  getFeatures,
  /**
   * @param {import('@enmon/commons/lib/enmon').Feature[]} features
   * @returns {(feature: import('@enmon/commons/lib/enmon').Feature) => boolean}
   */
  features => feature => includes(feature, features),
);

/**
 * @param {unknown} state
 * @returns {import('@enmon/commons/lib/enmon').Module[]}
 */
const getModules = state => path(['auth', 'customer', 'settings', 'modules'], state) || [];

/**
 * @deprecated Use `useHasModule` hook instead.
 */
export const selectHasModule = createSelector(
  getModules,
  /**
   * @param {import('@enmon/commons/lib/enmon').Module[]} modules
   * @returns {(module: import('@enmon/commons/lib/enmon').Module) => boolean}
   */
  modules => module => includes(module, modules),
);

/**
 *
 * @param {unknown} state
 * @returns {import('@enmon/commons/lib/enmon/shared').Currency}
 * @deprecated Use `useCustomerCurrency` hook instead.
 */
export const getCustomerCurrency = state => path(['auth', 'customer', 'settings', 'currency'], state);

/**
 * @deprecated Use `useCustomerCurrencyReadable` hook instead.
 */
export const selectCustomerCurrencyReadable = createSelector(
  getCustomerCurrency,
  /**
   * @param {import('@enmon/commons/lib/enmon/shared').Currency} currency
   * @returns {string | ''}
   */
  currency => CURRENCY_TRANSLATED[currency] || '',
);

export const getCustomerDefaultFilter = state => path(['auth', 'customer', 'settings', 'defaultAnalysisFilter'], state);
